import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock3 } from "lucide-react";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";
import { formatKioskPrice } from "./KioskPay";

interface KioskLocalOrder {
  id: string;
  status: "PENDING" | "PAID" | "FAILED" | "CANCELLED" | "REFUNDED";
  kioskNotifyStatus: "PENDING" | "DONE" | "MANUAL_REVIEW" | null;
  kioskCode: string | null;
  kioskSnapshot: {
    items?: { name: string; quantity: number; total: number }[];
    totalAmount?: number;
  } | null;
  paidAmount: number;
  errorMessage: string | null;
}

// Ödeme dönüşü: sipariş durumunu API'den okur. PENDING (callback gecikmesi) ve
// PAID+bildirim bekliyor durumlarında kısa süre yoklar.
export default function KioskPayResultPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId") || "";

  const [order, setOrder] = useState<KioskLocalOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollCountRef = useRef(0);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await ApiService.getApi(`kiosk/local-orders/${orderId}`);
      setOrder(res?.data || null);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Sipariş durumu alınamadı.");
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    fetchOrder();
    const interval = setInterval(() => {
      pollCountRef.current += 1;
      // ~1 dk sonra yoklamayı bırak; admin reconcile zaten kayıp callback'i toparlar
      if (pollCountRef.current > 20) {
        clearInterval(interval);
        return;
      }
      fetchOrder();
    }, 3000);
    return () => clearInterval(interval);
  }, [orderId, fetchOrder]);

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!orderId) {
    return <Navigate to="/kiosk-pay" replace />;
  }

  const paid = order?.status === "PAID";
  const failed = order && ["FAILED", "CANCELLED", "REFUNDED"].includes(order.status);
  const pending = order?.status === "PENDING";
  const items = order?.kioskSnapshot?.items || [];

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-md mx-auto w-full">
      <h1 className="text-xl font-bold text-white">Kiosk Ödeme Sonucu</h1>

      {!order && !error ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : error && !order ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : paid ? (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-green-200 rounded-xl p-8 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-600" />
            <div>
              <p className="text-lg font-bold text-gray-900">Ödeme Alındı</p>
              <p className="text-2xl font-extrabold text-green-700 mt-1 tabular-nums">
                {formatKioskPrice(order!.paidAmount)}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {order!.kioskNotifyStatus === "DONE"
                  ? "Satış işlendi — kiosk ekranı birazdan güncellenecek."
                  : order!.kioskNotifyStatus === "MANUAL_REVIEW"
                    ? "Ödemeniz alındı; satış kaydı görevli kontrolünde tamamlanacak."
                    : "Satış işleniyor — kiosk ekranı birazdan güncellenecek."}
              </p>
            </div>
          </div>

          {items.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Fiş · {order!.kioskCode}
                </p>
              </div>
              <div className="divide-y divide-gray-50">
                {items.map((item, i) => (
                  <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                    <span className="text-sm text-gray-700 flex-1 min-w-0">{item.name}</span>
                    <span className="text-xs text-gray-400 shrink-0">×{item.quantity}</span>
                    <span className="text-sm font-semibold text-gray-800 shrink-0 tabular-nums">
                      {formatKioskPrice(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Link
            to="/cafe-menu"
            className="w-full py-3 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-center"
          >
            Menüye Dön
          </Link>
        </div>
      ) : failed ? (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-red-200 rounded-xl p-8 flex flex-col items-center gap-3 text-center">
            <XCircle className="w-14 h-14 text-red-500" />
            <div>
              <p className="text-lg font-bold text-gray-900">Ödeme Tamamlanamadı</p>
              {order!.errorMessage && <p className="text-xs text-gray-500 mt-2">{order!.errorMessage}</p>}
            </div>
          </div>
          <Link
            to={order!.kioskCode ? `/kiosk-pay?code=${order!.kioskCode}` : "/kiosk-pay"}
            className="w-full py-3 rounded-xl bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 transition-colors text-center"
          >
            Tekrar Dene
          </Link>
        </div>
      ) : pending ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center gap-3 text-center">
          <Clock3 className="w-12 h-12 text-blue-500" />
          <div>
            <p className="text-sm font-semibold text-gray-800">Ödemeniz Kontrol Ediliyor</p>
            <p className="text-xs text-gray-400 mt-1">
              Banka onayı bekleniyor. Bu ekran kendiliğinden güncellenir; sonuç birazdan görünür.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
